import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStore, selectUser } from "@safers/auth";
import { useEventsStore, selectStompEvents } from "@/stores/events.store";
import { useAlarmInboxStore, selectLastSeenAt, selectMarkSeen } from "@/stores/alarm-inbox.store";
import { controlService, type DashboardEventItem, type EventSeverity } from "@safers/api";
import { byTimeDesc } from "@/services";
import { controlKeys } from "@/pages/control/useControlData";
import { shouldPulse } from "@/lib/map";
import { canSeeEvent } from "@/lib/eventVisibility";

export interface AlarmItem {
  eventId: string;
  id: number;
  name: string;
  siteId: number | null;
  siteName: string | null;
  severity: EventSeverity | null;
  category: string;
  timestamp: string;
  read: boolean;
}

function toItem(e: DashboardEventItem): AlarmItem {
  return {
    eventId: e.eventId,
    id: e.id,
    name: e.name,
    siteId: e.siteId,
    siteName: e.siteName,
    severity: e.severity,
    category: e.category,
    timestamp: e.timestamp,
    read: false,
  };
}

// 이력(대시보드 응답)과 실시간을 합친다. 실시간만 쓰면 화면 켠 뒤 것만 보이고,
// 이력만 쓰면 지금 들어오는 걸 놓친다.
export function useAlarmInbox() {
  const stompEvents = useEventsStore(selectStompEvents);

  const user = useAuthStore(selectUser);
  const username = user?.username ?? null;
  const lastSeenAt = useAlarmInboxStore(selectLastSeenAt(username));
  const markSeenFor = useAlarmInboxStore(selectMarkSeen);

  const markSeen = useCallback(() => {
    if (username) markSeenFor(username);
  }, [username, markSeenFor]);

  // 관제 화면과 **같은 쿼리 키**여야 한다 — 안 그러면 벨이 뜰 때마다 같은 응답을 또 받는다.
  const { data } = useQuery({
    queryKey: controlKeys.national(),
    queryFn: () => controlService.getAllSites(),
    staleTime: 60_000,
  });
  const history: DashboardEventItem[] = useMemo(() => byTimeDesc(data?.events ?? []), [data]);

  const items = useMemo<AlarmItem[]>(() => {
    const byId = new Map<string, AlarmItem>();
    history.forEach((e) => byId.set(e.eventId, toItem(e)));

    stompEvents.forEach((e) => {
      const known = byId.get(e.eventId);
      byId.set(e.eventId, {
        eventId: e.eventId,
        id: e.id,
        name: e.name,
        // 실시간 메시지가 현장을 싣고 온다. 이력 폴백은 서버가 빠뜨릴 때를 위한 그물이다.
        siteId: e.site?.id ?? known?.siteId ?? null,
        siteName: e.site?.name ?? known?.siteName ?? null,
        severity: e.severity ?? known?.severity ?? null,
        category: e.category,
        timestamp: e.timestamp,
        read: false,
      });
    });

    const seen = (it: AlarmItem) => {
      const t = new Date(it.timestamp).getTime();
      return !Number.isFinite(t) || t <= lastSeenAt;
    };

    return (
      [...byId.values()]
        // 열리지 않는 것은 목록에도 두지 않는다 — 상세가 403 이라 누르면 막다른 길이다.
        .filter((it) => canSeeEvent(user, it))
        .map((it) => ({ ...it, read: seen(it) }))
        .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    );
  }, [history, stompEvents, lastSeenAt, user]);

  // 뱃지는 주의 이상만 센다. 체감온도 "관심"이 매시간 현장마다 들어와서, 다 세면
  // 뱃지가 상시 켜진다 — 늘 켜져 있는 알림은 알림이 아니다. 목록에는 다 보여준다.
  const unreadCount = useMemo(
    () => items.filter((i) => !i.read && i.severity != null && shouldPulse(i.severity)).length,
    [items]
  );

  return { items, unreadCount, markSeen };
}
